import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Award, 
  Target, 
  Lightbulb, 
  Users, 
  Globe, 
  TrendingUp,
  Factory,
  CheckCircle,
  Wrench
} from "lucide-react";
import { companyInfo, companyHistory } from "../data/mock";
import { MilestoneRoadmap } from "../components/MilestoneRoadmap";

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-amber-500/20 text-amber-300 border-amber-500/30">About Us</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Pioneering Excellence in Coating & Converting Machinery Since 1977
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              With over 45 years of manufacturing excellence, Hero Impex India Private Limited has established itself as a trusted name in the coating, lamination, slitting, and converting machinery industry.
            </p>
          </div>
        </div>
      </section>

      {/* Company History - Enhanced Version with Founder Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Journey of Innovation</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded in <strong>1977</strong> by <strong>{companyInfo.founder}</strong>, a professional mechanical engineer with experience from a West German company, Hero Impex was born with a clear mission: to substitute imports and establish indigenous manufacturing capabilities in India's coating, printing, and converting industry.
                </p>
                <p>
                  Starting from humble beginnings, we pioneered numerous groundbreaking solutions that transformed how Indian manufacturers approached tape and paper production. Our commitment to import substitution and self-reliance became the cornerstone of India's manufacturing revolution in this sector.
                </p>
                <p>
                  In <strong>1997</strong>, we incorporated as a Private Limited Company, marking a significant milestone in our journey toward becoming a globally recognized brand. Today, with over <strong>5000 projects completed in 20+ countries</strong>, we continue to set new benchmarks in machinery excellence.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="w-full h-80 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 rounded-xl shadow-lg flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <Factory className="w-20 h-20 mx-auto mb-4 text-cyan-400" />
                  <h3 className="text-2xl font-bold mb-2">Manufacturing Excellence</h3>
                  <p className="text-slate-300">45+ Years of Industry Leadership</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-amber-600 text-white">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl font-bold mb-2">{companyInfo.machinesDelivered}</div>
                    <div className="text-sm text-cyan-100">Machines Delivered</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 text-white">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl font-bold mb-2">{companyInfo.experience}</div>
                    <div className="text-sm text-slate-300">Years of Excellence</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Prominent Milestones Timeline */}
          <div className="max-w-6xl mx-auto mt-16">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">Our Milestones</Badge>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">45+ Years of Innovation</h3>
              <p className="text-slate-600 max-w-2xl mx-auto">
                From our inception to becoming a global leader, every milestone marks our commitment to excellence and innovation in machinery manufacturing.
              </p>
            </div>
            
            <MilestoneRoadmap milestones={companyHistory} />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-t-4 border-amber-600">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  {companyInfo.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-amber-600">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                  <Lightbulb className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  {companyInfo.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The principles that guide our operations and define our commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Quality Excellence</h3>
                <p className="text-slate-600">
                  Uncompromising commitment to delivering machines built to the highest quality standards with rigorous testing and certification.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Customer Focus</h3>
                <p className="text-slate-600">
                  Dedicated to understanding and exceeding customer expectations through personalized solutions and exceptional after-sales support.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Innovation</h3>
                <p className="text-slate-600">
                  Continuous investment in research and development to incorporate latest technologies and improve machine performance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Integrity</h3>
                <p className="text-slate-600">
                  Operating with transparency, honesty, and ethical business practices in all our dealings with customers and partners.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Reliability</h3>
                <p className="text-slate-600">
                  Building machines that deliver consistent performance year after year with minimal downtime and maintenance requirements.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Global Standards</h3>
                <p className="text-slate-600">
                  Adhering to international quality certifications and German engineering standards in every machine we manufacture.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Manufacturing Excellence</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              State-of-the-art facilities and skilled workforce delivering world-class machinery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-amber-600 mb-2">10,000+</div>
                <div className="text-sm text-slate-600">Sq. Ft. Manufacturing Area</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-amber-600 mb-2">50+</div>
                <div className="text-sm text-slate-600">Skilled Engineers & Technicians</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">ISO</div>
                <div className="text-sm text-slate-600">Quality Certified Facility</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">24/7</div>
                <div className="text-sm text-slate-600">Production Capability</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

